import type { TranslationFunction } from "@src/packages/i18n";
import type { HeroMetric } from "@src/packages/homepage/plan/components/hero/Hero";
import type { CycleHeaderState } from "../hooks/resolveCycleHeaderState";
import { formatPhaseTime, formatRelativePhaseDate } from "./formatPhaseDate";

const PREFIX = "homepage.review_concept.hero_dynamic.metric";

export interface HeroMetricsContext {
  /** Lead follicle size in mm, latest measurement. */
  leadFollicleMm: number | null;
  /** Latest estradiol reading. */
  estradiol: { value: number; unit: string } | null;
  /** Endometrial thickness in mm, latest. */
  endometriumMm: number | null;
  /** Estimated retrieval date (real or +12d from stim start). */
  estimatedRetrievalDate: string | null;
  /** Days since transfer, if a transfer phase exists with a startDate. */
  daysPostTransfer: number | null;
  /** Count of distinct medication doses scheduled for today. */
  todayMedDoseCount: number;
}

interface BuildArgs {
  state: CycleHeaderState;
  context: HeroMetricsContext;
  t: TranslationFunction;
  locale: string | undefined;
  now?: Date;
}

function metric(
  label: string,
  value: string | null | undefined,
  note?: string | null,
): HeroMetric | null {
  if (value == null || value === "") return null;
  return note ? { label, value, note } : { label, value };
}

/**
 * Build the Hero KPI grid for the homepage. Returns at most 4 metrics;
 * each one is rendered only if its source value exists. If every metric
 * is null we return an empty array — the Hero hides the block.
 */
export function buildHeroMetrics(args: BuildArgs): HeroMetric[] {
  const { state, context, t, locale } = args;
  const now = args.now ?? new Date();

  const fmtDate = (iso: string | null | undefined) =>
    iso ? formatRelativePhaseDate(iso, locale, t, { now }) : null;

  switch (state.kind) {
    case "stim_trigger_logged": {
      const triggerWhen = state.triggerDate
        ? `${fmtDate(state.triggerDate)}${state.triggerTime ? ` ${formatPhaseTime(state.triggerTime, locale)}` : ""}`
        : null;
      return [
        metric(t(`${PREFIX}.trigger`), triggerWhen),
        metric(
          t(`${PREFIX}.retrieval_estimate`),
          fmtDate(context.estimatedRetrievalDate),
        ),
      ].filter((x): x is HeroMetric => !!x);
    }

    case "stim_monitoring_next": {
      return [
        metric(t(`${PREFIX}.next_monitoring`), fmtDate(state.apptDate)),
        metric(
          t(`${PREFIX}.lead_follicle`),
          context.leadFollicleMm != null
            ? `${Math.round(context.leadFollicleMm)} mm`
            : null,
        ),
        metric(
          t(`${PREFIX}.estradiol`),
          context.estradiol
            ? `${Math.round(context.estradiol.value)} ${context.estradiol.unit}`
            : null,
        ),
      ].filter((x): x is HeroMetric => !!x);
    }

    case "stim_med_next": {
      return [
        metric(
          t(`${PREFIX}.next_injection`),
          `${fmtDate(state.medDate)} · ${formatPhaseTime(state.medTime, locale)}`,
        ),
        metric(
          t(`${PREFIX}.lead_follicle`),
          context.leadFollicleMm != null
            ? `${Math.round(context.leadFollicleMm)} mm`
            : null,
        ),
      ].filter((x): x is HeroMetric => !!x);
    }

    case "lining_prep": {
      return [
        metric(t(`${PREFIX}.transfer_date`), fmtDate(state.transferDate)),
        metric(
          t(`${PREFIX}.next_lining_check`),
          fmtDate(state.nextMonitoringDate),
        ),
        metric(
          t(`${PREFIX}.endometrium`),
          context.endometriumMm != null
            ? `${Math.round(context.endometriumMm * 10) / 10} mm`
            : null,
        ),
      ].filter((x): x is HeroMetric => !!x);
    }

    case "stim_generic": {
      return [
        metric(
          t(`${PREFIX}.lead_follicle`),
          context.leadFollicleMm != null
            ? `${Math.round(context.leadFollicleMm)} mm`
            : null,
        ),
        metric(
          t(`${PREFIX}.estradiol`),
          context.estradiol
            ? `${Math.round(context.estradiol.value)} ${context.estradiol.unit}`
            : null,
        ),
        metric(
          t(`${PREFIX}.retrieval_estimate`),
          fmtDate(context.estimatedRetrievalDate),
        ),
      ].filter((x): x is HeroMetric => !!x);
    }

    case "retrieval_scheduled": {
      return [
        metric(t(`${PREFIX}.retrieval_date`), fmtDate(state.retrievalDate)),
      ].filter((x): x is HeroMetric => !!x);
    }

    case "retrieval_done_pending": {
      return [
        metric(t(`${PREFIX}.retrieved_on`), fmtDate(state.retrievalDate)),
      ].filter((x): x is HeroMetric => !!x);
    }

    case "embryo_dev": {
      return [
        metric(t(`${PREFIX}.transfer_date`), fmtDate(state.transferDate)),
        metric(
          t(`${PREFIX}.endometrium`),
          context.endometriumMm != null
            ? `${Math.round(context.endometriumMm * 10) / 10} mm`
            : null,
        ),
      ].filter((x): x is HeroMetric => !!x);
    }

    case "transfer_scheduled": {
      return [
        metric(t(`${PREFIX}.transfer_date`), fmtDate(state.transferDate)),
        metric(
          t(`${PREFIX}.endometrium`),
          context.endometriumMm != null
            ? `${Math.round(context.endometriumMm * 10) / 10} mm`
            : null,
        ),
      ].filter((x): x is HeroMetric => !!x);
    }

    case "beta_scheduled": {
      // Day-of-wait is in the hero title and the blood-test date now sits on the
      // progress bar's end label, so the metrics carry only today's meds.
      return [
        metric(
          t(`${PREFIX}.meds_today`),
          context.todayMedDoseCount > 0 ? `${context.todayMedDoseCount}` : null,
        ),
      ].filter((x): x is HeroMetric => !!x);
    }

    case "beta_pending": {
      return [
        metric(
          t(`${PREFIX}.meds_today`),
          context.todayMedDoseCount > 0 ? `${context.todayMedDoseCount}` : null,
        ),
      ].filter((x): x is HeroMetric => !!x);
    }

    case "no_cycle":
    case "cycle_closed":
    case "pre_treatment":
    case "unknown_active":
    case "fertilization":
    case "insemination":
    default:
      return [];
  }
}
